import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('ltcPreferences');
export interface LTCPreferencesData {
  id?: string;
  userId?: string;
  inHomeCareSelectedOptions?: string[];
  inHomeCareAdditionalPreferences?: string;
  adultDaycareSelectedOptions?: string[];
  adultDaycareAdditionalPreferences?: string;
  assistedLivingSelectedOptions?: string[];
  assistedLivingAdditionalPreferences?: string;
  memoryCareSelectedOptions?: string[];
  memoryCareAdditionalPreferences?: string;
  nursingHomeSelectedOptions?: string[];
  nursingHomeAdditionalPreferences?: string;
  hospiceSelectedOptions?: string[];
  hospiceAdditionalPreferences?: string;
  respiteCareSelectedOptions?: string[];
  respiteCareAdditionalPreferences?: string;
  continuedCareSelectedOptions?: string[];
  continuedCareAdditionalPreferences?: string;
  doesNotApply?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

class LTCPreferencesService {
  private async getAuthHeaders() {
    const token = await getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async createLTCPreferences(data: Partial<LTCPreferencesData>): Promise<LTCPreferencesData> {
    try {
      const headers = await this.getAuthHeaders();

      logger.debug('[LTC Preferences Service] Creating LTC preferences:', data);

      const response = await fetch(`${API_URL}/api/ltc-preferences`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[LTC Preferences Service] Create failed:', response.status, errorText);
        throw new Error(`Failed to create LTC preferences: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[LTC Preferences Service] Create successful:', result);

      if (result.success && result.data) {
        return result.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      logger.error('[LTC Preferences Service] Create error:', error);
      throw error;
    }
  }

  async updateLTCPreferences(id: string, data: Partial<LTCPreferencesData>): Promise<LTCPreferencesData> {
    try {
      const headers = await this.getAuthHeaders();

      logger.debug('[LTC Preferences Service] Updating LTC preferences:', id, data);

      const response = await fetch(`${API_URL}/api/ltc-preferences/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[LTC Preferences Service] Update failed:', response.status, errorText);
        throw new Error(`Failed to update LTC preferences: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[LTC Preferences Service] Update successful:', result);

      if (result.success && result.data) {
        return result.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      logger.error('[LTC Preferences Service] Update error:', error);
      throw error;
    }
  }

  async getLTCPreferencesById(id: string): Promise<LTCPreferencesData | null> {
    try {
      const headers = await this.getAuthHeaders();

      logger.debug('[LTC Preferences Service] Getting LTC preferences by ID:', id);

      const response = await fetch(`${API_URL}/api/ltc-preferences/${id}`, {
        method: 'GET',
        headers,
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[LTC Preferences Service] Get by ID failed:', response.status, errorText);
        throw new Error(`Failed to get LTC preferences: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[LTC Preferences Service] Get by ID successful:', result);

      if (result.success && result.data) {
        return result.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      logger.error('[LTC Preferences Service] Get by ID error:', error);
      throw error;
    }
  }

  async getAllLTCPreferences(): Promise<LTCPreferencesData[]> {
    try {
      const headers = await this.getAuthHeaders();

      logger.debug('[LTC Preferences Service] Getting all LTC preferences');

      const response = await fetch(`${API_URL}/api/ltc-preferences`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[LTC Preferences Service] Get all failed:', response.status, errorText);
        throw new Error(`Failed to get LTC preferences: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[LTC Preferences Service] Get all successful:', result);

      if (result.success && result.data) {
        return result.data;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      logger.error('[LTC Preferences Service] Get all error:', error);
      throw error;
    }
  }

  async deleteLTCPreferences(id: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();

      logger.debug('[LTC Preferences Service] Deleting LTC preferences:', id);

      const response = await fetch(`${API_URL}/api/ltc-preferences/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[LTC Preferences Service] Delete failed:', response.status, errorText);
        throw new Error(`Failed to delete LTC preferences: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[LTC Preferences Service] Delete successful:', result);
    } catch (error) {
      logger.error('[LTC Preferences Service] Delete error:', error);
      throw error;
    }
  }

  async lockLTCPreferences(id: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();

      logger.debug('[LTC Preferences Service] Locking LTC preferences:', id);

      const response = await fetch(`${API_URL}/api/ltc-preferences/${id}/lock`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[LTC Preferences Service] Lock failed:', response.status, errorText);
        throw new Error(`Failed to lock LTC preferences: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[LTC Preferences Service] Lock successful:', result);
    } catch (error) {
      logger.error('[LTC Preferences Service] Lock error:', error);
      throw error;
    }
  }

  async unlockLTCPreferences(id: string, emails: string[]): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();

      logger.debug('[LTC Preferences Service] Unlocking LTC preferences:', id, emails);

      const response = await fetch(`${API_URL}/api/ltc-preferences/${id}/unlock`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ emails }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[LTC Preferences Service] Unlock failed:', response.status, errorText);
        throw new Error(`Failed to unlock LTC preferences: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[LTC Preferences Service] Unlock successful:', result);
    } catch (error) {
      logger.error('[LTC Preferences Service] Unlock error:', error);
      throw error;
    }
  }

  async getLTCPreferencesAccessGrants(id: string): Promise<any[]> {
    try {
      const headers = await this.getAuthHeaders();

      logger.debug('[LTC Preferences Service] Getting LTC preferences access grants:', id);

      const response = await fetch(`${API_URL}/api/ltc-preferences/${id}/grants`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[LTC Preferences Service] Get grants failed:', response.status, errorText);
        throw new Error(`Failed to get LTC preferences access grants: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[LTC Preferences Service] Get grants successful:', result);

      if (result.success && result.data) {
        return result.data;
      } else {
        return [];
      }
    } catch (error) {
      logger.error('[LTC Preferences Service] Get grants error:', error);
      throw error;
    }
  }
}

export const ltcPreferencesService = new LTCPreferencesService();